import { condition, getWidgetScalePosition } from '../Announcement';
import { Log } from '../../../AuxillaryService/Log';
import { labOptions, Signage } from '../../../Signage';
import { moment } from '../../../../vendor/moment';
import { NamedEventCode } from '@reachmedianetwork/signage-abstractions';
import { AnnouncementBaseConfiguration, AnnouncementState, AnnouncementStatus } from '../types';
import { AnnouncementWidget } from '../AnnouncementWidget';
import { isNullish } from '../../../Utility/Nullish';
import type { PlayTimes } from '../PlayTimes';

/**
 * **NOTICE**
 * The proof of play log messages are not using the traditional __fileline
 * subcategory due to how they are being collected in the C# software.
 * This is a hack-y solution that is intended to be fixed.
 */

export abstract class AnnouncementBase {
    constructor(
        configuration: AnnouncementBaseConfiguration,
        widget: AnnouncementWidget,
        playTimes: PlayTimes
    ) {
        this.configuration = {
            ...configuration,
            //override background_color if set at the widget level
            ...(widget.config.background_color
                ? { background_color: widget.config.background_color }
                : {}),
        };
        this.widget = widget;

        this.#startDate = moment(configuration.start, 'YYYY-MM-DD');
        this.#endDate = moment(configuration.end, 'YYYY-MM-DD').endOf('day');
        this.#playTimes = playTimes;
    }

    protected readonly widget: AnnouncementWidget;

    public configuration: AnnouncementBaseConfiguration & { template: string };

    #playTimes: PlayTimes;
    public get playTimes(): PlayTimes {
        return this.#playTimes;
    }

    /**
     * moment date object - start date
     * @TODO: move to Luxon
     */
    #startDate?: moment.Moment;

    /**
     * moment date object - end date
     * @TODO: move to Luxon
     */
    #endDate?: moment.Moment;

    #html = '';

    #state: AnnouncementState = AnnouncementState.UNPROVISIONED;

    /**
     * true if provision is locked
     */
    public provisionIsLocked = false;

    /**
     * moment date object - when announcement displays
     */
    #startPlay?: moment.Moment;

    /**
     * if announcement is currently displaying
     */
    #isPlaying = false;

    /**
     * if announcement eventually needs unprovisioning
     */
    #needsUnprovisioning = false;

    /**
     * determines if announcement is playing for the first time
     */
    public firstRun = true;

    /**
     * The ID of the element containing the announcement
     */
    protected get containerElementId(): string {
        return `announcement_${this.configuration.id}_screen_${this.screenId}_widget_${this.widget.id}`;
    }

    protected abstract renderTemplate(): string;

    /**
     * Returns the duration of the announcement content.
     */
    public get duration(): number {
        // if lab option "announcementDurationOverride" > 0, use that
        // instead of announcement actual duration
        const announcementDurationOverride = labOptions.instance.get(
            'announcementDurationOverride'
        );
        const durationOverride = announcementDurationOverride || -1;
        return durationOverride > 0 ? durationOverride : this.configuration.timing;
    }

    /**
     * announcement facility ID
     */
    private get facilityId(): number {
        return parseInt(this.widget.config.facilityid);
    }

    /**
     * announcement screen ID
     */
    public get screenId(): number {
        return parseInt(this.widget.config.screenid);
    }

    /**
     * sets and returns new announcement order
     */
    public order(newOrder?: number): number {
        if (!isNullish(newOrder)) {
            this.configuration.order = newOrder;
        }

        this.widget.wrapperElement
            .querySelector(`#${this.containerElementId}`)
            ?.setAttribute('data-announcement-order', this.configuration.order.toString());

        return this.configuration.order;
    }

    /**
     * determines if announcement is eligible for play
     */
    public canPlay(): boolean {
        try {
            if (this.getStatus() !== condition.OK) {
                //the announcement itself says it's not ready to play!
                Log.Warning(
                    `announcement \`${this.configuration.name}\` with id \`${this.configuration.id}\` reports condition \`PENDING\` and will not play now`,
                    null,
                    'announcement.js', // **See note about hardcoded source
                    { announcement: this.configuration }
                );

                return false;
            }

            //todo: convert to Luxon
            const now = moment();

            if (
                this.configuration.status !== AnnouncementStatus.ACTIVE ||
                !now.isBetween(this.#startDate, this.#endDate)
            ) {
                return false;
            }

            return this.#playTimes.canPlay(now);
        } catch (e) {
            Log.Error(
                'Failed to determine if announcement can play: ' + String(e),
                null,
                'announcement.js', // **See note about hardcoded source
                { error: e }
            );
        }

        return false;
    }

    public async safeUnprovision(): Promise<void> {
        if (this.#isPlaying) {
            this.#needsUnprovisioning = true;
        } else {
            await this.unprovision();
        }
    }

    public async unprovision(): Promise<void> {
        if (this.#state === AnnouncementState.PROVISIONED) {
            try {
                await this.deallocate();
            } catch (e) {
                Log.Error(
                    'Deallocation Failed: ' + String(e),
                    null,
                    'announcement.js', // **See note about hardcoded source
                    { error: e }
                );
            }

            //remove the div
            this.widget.wrapperElement.querySelector(`#${this.containerElementId}`)?.remove();

            this.#state = AnnouncementState.UNPROVISIONED;
        }
    }

    public async provision(): Promise<void> {
        //look to see if announcement is currently provisioned, if not run code
        if (this.#state === AnnouncementState.PROVISIONED) {
            return;
        }

        this.#html = this.renderTemplate();
        this.#state = AnnouncementState.PROVISIONED;

        this.widget.wrapperElement
            .querySelector('.announcement-container-class')
            ?.insertAdjacentHTML('beforeend', this.#html);

        try {
            await this.initialize();
        } catch (error) {
            Log.Error(
                `Announcement(id:${this.configuration.id}) failed to initialize: ${error}`,
                null,
                'announcement.js', // **See note about hardcoded source
                { error: error, announcementId: this.configuration.id }
            );
        }
    }

    public getAndSetStartDate(newStart?: string): moment.Moment {
        if (typeof newStart !== 'undefined') {
            this.configuration.start = newStart;
            this.#startDate = undefined;
        }

        if (!this.#startDate) {
            this.#startDate = moment(this.configuration.start, 'YYYY-MM-DD');
        }

        return this.#startDate;
    }

    public getAndSetEndDate(newEnd?: string): moment.Moment {
        if (typeof newEnd !== 'undefined') {
            this.configuration.end = newEnd;
            this.#endDate = undefined;
        }

        if (!this.#endDate) {
            this.#endDate = moment(this.configuration.end, 'YYYY-MM-DD');
        }

        return this.#endDate;
    }

    /**
     * Does announcement updating and returns true/false if the announcement has
     * changed at all
     */
    public update(newAnnouncement: AnnouncementBase): boolean {
        if (this.configuration.updated === newAnnouncement.configuration.updated) {
            return false;
        }

        if (this.configuration.start !== newAnnouncement.configuration.start) {
            this.configuration.start = newAnnouncement.configuration.start;
            this.#startDate = undefined;
        }

        if (this.configuration.end !== newAnnouncement.configuration.end) {
            this.configuration.end = newAnnouncement.configuration.end;
            this.#endDate = undefined;
        }

        this.order(newAnnouncement.order());
        this.#playTimes = newAnnouncement.playTimes;

        this.#startDate = newAnnouncement.getAndSetStartDate();
        this.#endDate = newAnnouncement.getAndSetEndDate();

        this.safeUnprovision().then();

        this.configuration = newAnnouncement.configuration;

        return true;
    }

    /**
     * Displays Announcement
     * @fires AnnouncementWidget#event:show-fullscreen-announcement
     */
    public showAnnouncement = async (): Promise<void> => {
        //add class OnDeckContainer, then fade to opacity 1

        if (this.configuration.go_full_screen === 'true') {
            this.widget.eventEmitter.emitEvent('show-fullscreen-announcement');
        }

        const maybeCanvasElement = this.widget.wrapperElement.querySelector<HTMLElement>(
            '.announcement-container-canvas-class'
        );
        if (maybeCanvasElement) {
            Object.assign(
                maybeCanvasElement.style,
                getWidgetScalePosition(
                    this.widget.wrapperElement.clientWidth,
                    this.widget.wrapperElement.clientHeight,
                    this.configuration.width,
                    this.configuration.height
                )
            );
        }

        const containerElement = this.widget.wrapperElement.querySelector<HTMLElement>(
            `#${this.containerElementId}`
        );
        containerElement?.classList.remove('OffscreenContainer');
        containerElement?.classList.add('OnDeckContainer');

        // cleanup to make sure only 1 container is showing
        const loadedContainer = this.widget.wrapperElement.querySelector(
            '.announcement-container-class > .LoadedContainer'
        );
        loadedContainer?.classList.remove('LoadedContainer');
        loadedContainer?.classList.add('OffscreenContainer');

        if (containerElement) {
            containerElement.style.opacity = '1';
            containerElement.classList.remove('OffscreenContainer');
            containerElement.classList.remove('OnDeckContainer');
            containerElement.classList.add('LoadedContainer');
        }

        this.#isPlaying = true;
        this.#startPlay = moment();
    };

    /**
     * Hides Announcement
     * @fires AnnouncementWidget#event:hide-fullscreen-announcement
     */
    public hideAnnouncement = async (nextAnnouncementFullscreen: boolean): Promise<void> => {
        if (this.configuration.go_full_screen === 'true' && !nextAnnouncementFullscreen) {
            this.widget.eventEmitter.emitEvent('hide-fullscreen-announcement');
        }

        //fade out, remove loadedcontainer class, add OffscreenContainer
        const maybeElem = this.widget.wrapperElement.querySelector<HTMLElement>(
            `#${this.containerElementId}`
        );
        if (maybeElem) {
            maybeElem.style.opacity = '0';
            maybeElem.classList.remove('LoadedContainer');
            maybeElem.classList.add('OffscreenContainer');
        }

        if (this.#isPlaying) {
            this.#isPlaying = false;
            this.recordPlay();
        }

        if (this.#needsUnprovisioning) {
            await this.unprovision();
            this.#needsUnprovisioning = false;
        }
    };

    /**
     * set up for on deck
     */
    public async onDeck(): Promise<void> {
        //may be overridden in implementing class
    }

    /**
     * announcement initialize method
     *
     * compiles content body, sets up 15 minute update interval
     */
    public async initialize(): Promise<void> {
        //may be overridden in implementing class
    }

    /**
     * gets announcement status
     */
    public getStatus(): condition {
        return condition.OK;
    }

    /**
     * set up for preparation
     */
    public prepPage(): void {
        //may be overridden in implementing class
    }

    /**
     * deallocate removes update timeout
     */
    public async deallocate(): Promise<void> {
        //may be overridden in implementing class
    }

    /**
     * begin transitions
     */
    public beginTransition(): void {
        //may be overridden in implementing class
    }

    /**
     * announcement run page method
     */
    public async runPage(): Promise<void> {
        //may be overridden in implementing class
    }

    /**
     * announcement stop page method
     */
    public async stopPage(): Promise<void> {
        //may be overridden in implementing class
    }

    protected recordPlay = (): void => {
        if (null !== this.#startPlay && Signage.enabledProofOfPlay) {
            const ms = moment().diff(this.#startPlay);
            const d = moment.duration(ms);

            if (this.widget.config.is_new_media_library) {
                Log.NamedEvent(
                    NamedEventCode.SIGNAGE_PLAY_EVENT,
                    'announcement played',
                    'announcement.js',
                    {
                        Type: 'media_library',
                        Id: this.configuration.id,
                        Name: this.configuration.name,
                        WidgetId: this.widget.id,
                        FacilityId: this.facilityId,
                        ScreenId: this.screenId,
                        StartTimeUtc: this.#startPlay?.unix() || Date.now(),
                        Duration: `${Math.floor(d.asHours())}${moment.utc(ms).format(':mm:ss')}`,
                        MediaLibraryItemId: this.configuration.media_library_item_id || -1,
                    }
                );
            } else {
                Log.NamedEvent(
                    NamedEventCode.SIGNAGE_PLAY_EVENT,
                    'announcement played',
                    'announcement.js',
                    {
                        Type: 'announcement',
                        Id: this.configuration.id,
                        Name: this.configuration.name,
                        WidgetId: this.widget.id,
                        FacilityId: this.facilityId,
                        ScreenId: this.screenId,
                        StartTimeUtc: this.#startPlay?.unix() || Date.now(),
                        Duration: `${Math.floor(d.asHours())}${moment.utc(ms).format(':mm:ss')}`,
                        MediaLibraryItemId: null,
                    }
                );
            }

            this.#startPlay = undefined;
        }
    };

    public async halt(): Promise<void> {
        await this.stopPage();
        await this.unprovision();
    }
}
